import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { PlusIcon } from '@phosphor-icons/react'
import { useCreateEditorCompetencyMutation } from '../../redux/growth-wheel.endpoints'
import type {
  Competency,
  SubCompetency,
} from '../../types/GrowthWheelManagement'
import GrowthWheelGraph from '../GrowthWheelVisualizer/growth-wheel-graph'
import { AddCompetencyForm } from './AddCompetencyForm'
import { WheelManagementCompetencyCard } from './WheelManagementCompetencyCard'
import type { RootState } from '../../redux/store'

interface WheelManagementProps {
  growthWheelId: number
  competencies: Competency[]
  subCompetencies: SubCompetency[]
  roleName?: string
  topSlot?: React.ReactNode
}

export function WheelManagement({
  growthWheelId,
  competencies,
  subCompetencies,
  roleName,
  topSlot,
}: WheelManagementProps) {
  const isReadOnly = useSelector(
    (state: RootState) => state.growthWheel.isReadOnly,
  )
  const [createCompetency] = useCreateEditorCompetencyMutation()

  const [showAddCompetency, setShowAddCompetency] = useState(false)
  const [newCompetencyName, setNewCompetencyName] = useState('')
  const [newCompetencyDesc, setNewCompetencyDesc] = useState('')

  const [competencyDropTargetIndex, setCompetencyDropTargetIndex] = useState<
    number | null
  >(null)

  useEffect(() => {
    if (!isReadOnly) return
    setShowAddCompetency(false)
    setNewCompetencyName('')
    setNewCompetencyDesc('')
    setCompetencyDropTargetIndex(null)
  }, [isReadOnly])

  const handleAddCompetency = async () => {
    if (isReadOnly) return
    if (!newCompetencyName.trim()) return
    await createCompetency({
      growthWheelId,
      body: {
        name: newCompetencyName.trim(),
        description: newCompetencyDesc.trim(),
      },
    }).unwrap()
    setNewCompetencyName('')
    setNewCompetencyDesc('')
    setShowAddCompetency(false)
  }

  return (
    <div className={`gw-wm${isReadOnly ? ' gw-wm--read-only' : ''}`}>
      <div>
        <h1 className="gw-wm__page-title">Growth Wheel Management</h1>
        <p className="gw-wm__lead">
          Configure the competency framework structure and content for your
          organization.
        </p>
      </div>

      {topSlot}

      <GrowthWheelGraph
        areas={competencies.map(competency => ({
          id: competency.id,
          name: competency.name,
          subCompetencies: subCompetencies.filter(
            subCompetency => subCompetency.competency === competency.id,
          ),
        }))}
        title="Growth Wheel Image"
        className="gw-wm__graph-card"
        sideContent={
          <div className="gw-wm__graph-side">
            <div className="gw-wm__role-context">
              <p className="gw-wm__role-context-label">Configuring wheel for</p>
              <p className="gw-wm__role-context-name">
                {roleName ?? 'this role'}
              </p>
            </div>
          </div>
        }
      />

      <div className="gw-wm__card">
        <div className="gw-wm__framework-head">
          <div>
            <h2 className="gw-wm__card-title gw-wm__card-title--flush">
              Competencies
            </h2>
          </div>
          {!isReadOnly ? (
            <button
              type="button"
              className="gw-wm__btn-primary"
              onClick={() => setShowAddCompetency(true)}
            >
              <PlusIcon size={16} weight="regular" aria-hidden />
              Add Competency
            </button>
          ) : null}
        </div>

        {competencies.length === 0 && !showAddCompetency && (
          <div className="gw-wm__empty">
            <div className="gw-wm__empty-icon">
              <PlusIcon weight="regular" aria-hidden />
            </div>
            <p className="gw-wm__hint mb-4">No competencies yet</p>
            {!isReadOnly ? (
              <button
                type="button"
                className="gw-wm__btn-primary"
                onClick={() => setShowAddCompetency(true)}
              >
                Add your first competency
              </button>
            ) : null}
          </div>
        )}

        {showAddCompetency && !isReadOnly && (
          <AddCompetencyForm
            newAreaName={newCompetencyName}
            newAreaDesc={newCompetencyDesc}
            onChangeName={setNewCompetencyName}
            onChangeDesc={setNewCompetencyDesc}
            onSubmit={() => void handleAddCompetency()}
            onCancel={() => {
              setShowAddCompetency(false)
              setNewCompetencyName('')
              setNewCompetencyDesc('')
            }}
          />
        )}

        <div
          className="gw-wm__area-list"
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setCompetencyDropTargetIndex(null)
            }
          }}
        >
          {competencies.map((competency, index) => (
            <WheelManagementCompetencyCard
              key={competency.id}
              growthWheelId={growthWheelId}
              competency={competency}
              index={index}
              allCompetencies={competencies}
              allSubCompetencies={subCompetencies}
              roleName={roleName}
              competencyDropTargetIndex={competencyDropTargetIndex}
              onCompetencyDropTargetChange={setCompetencyDropTargetIndex}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
